import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
  data?: any;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
}

interface TreeViewProps {
  nodes: TreeNode[];
  onSelect: (node: TreeNode) => void;
  selectedId?: string;
  expandedByDefault?: boolean;
  className?: string;
}

const TreeViewItem: React.FC<{
  node: TreeNode;
  level: number;
  onSelect: (node: TreeNode) => void;
  selectedId?: string;
  expandedByDefault?: boolean;
}> = ({ node, level, onSelect, selectedId, expandedByDefault = false }) => {
  const [expanded, setExpanded] = useState(expandedByDefault);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleSelect = () => {
    onSelect(node);
  };

  return (
    <div className="select-none">
      <div
        className={`flex items-center py-1.5 px-2 rounded-md cursor-pointer transition-colors ${
          isSelected
            ? 'bg-primary/20 text-primary'
            : 'hover:bg-background-hover text-gray-300'
        }`}
        style={{ paddingLeft: `${(level + 1) * 12}px` }}
        onClick={handleSelect}
      >
        <div className="mr-1.5" onClick={hasChildren ? toggleExpand : undefined}>
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )
          ) : (
            <div className="w-4" />
          )}
        </div>
        {node.icon && <div className="mr-2">{node.icon}</div>}
        <span className="flex-1 truncate">{node.name}</span>
        {node.badge && <div className="ml-2">{node.badge}</div>}
      </div>
      
      {expanded && hasChildren && (
        <div className="mt-0.5">
          {node.children!.map((child) => (
            <TreeViewItem
              key={child.id}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              selectedId={selectedId}
              expandedByDefault={expandedByDefault}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TreeView: React.FC<TreeViewProps> = ({
  nodes,
  onSelect,
  selectedId,
  expandedByDefault = false,
  className = '',
}) => {
  return (
    <div className={`overflow-y-auto ${className}`}>
      {nodes.map((node) => (
        <TreeViewItem
          key={node.id}
          node={node}
          level={0}
          onSelect={onSelect}
          selectedId={selectedId}
          expandedByDefault={expandedByDefault}
        />
      ))}
    </div>
  );
};

export default TreeView;
